import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faFileExcel,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";
import "./css/HistoryKaizen.css";

export default function HistoryKaizenTab() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/maintenance/kaizen/history");
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("HISTORY KAIZEN ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = data.filter((row) => {
    const keyword = search.toLowerCase();
    const rowDate = row.tanggal
      ? new Date(row.tanggal).toISOString().split("T")[0]
      : "";

    const matchKeyword =
      !keyword ||
      row.mesin?.toLowerCase().includes(keyword) ||
      row.pic?.toLowerCase().includes(keyword) ||
      row.material_name?.toLowerCase().includes(keyword);

    const matchDateFrom = !dateFrom || rowDate >= dateFrom;
    const matchDateTo = !dateTo || rowDate <= dateTo;

    return matchKeyword && matchDateFrom && matchDateTo;
  });

  const exportToExcel = () => {
    if (filtered.length === 0) return popup.alert("Data kosong");

    const exportData = filtered.map((row, index) => ({
      No: index + 1,
      Tanggal: row.tanggal
        ? new Date(row.tanggal).toLocaleDateString("id-ID")
        : "-",
      Mesin: row.mesin,
      PIC: row.pic,
      Material: row.material_name,
      Qty: row.qty,
      Keterangan: row.note,
      "Created At": row.created_at
        ? new Date(row.created_at).toLocaleString("id-ID")
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "History Kaizen");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `History_Kaizen_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const resetFilter = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const handleDelete = async (id) => {
    const ok = await popup.confirm("Yakin hapus history kaizen ini?");
    if (!ok) return;
    try {
      await api.delete(`/maintenance/kaizen/history/${id}`);
      setData((prev) => prev.filter((row) => row.id !== id));
      popup.success("Data berhasil dihapus");
    } catch (err) {
      console.error(err);
      popup.alert("❌ Gagal hapus history kaizen");
    }
  };

  return (
    <div className="hk-container">
      <div className="hk-card">
        {/* HEADER */}
        <div className="hk-header">
          <div className="hk-title-wrap">
            <span className="hk-icon">
              <FontAwesomeIcon icon={faLightbulb} />
            </span>
            <div>
              <h5 className="hk-title">History Kaizen</h5>
              <p className="hk-subtitle">{filtered.length} data pemakaian</p>
            </div>
          </div>

          <button className="hk-btn-excel" onClick={exportToExcel}>
            <FontAwesomeIcon icon={faFileExcel} /> Export Excel
          </button>
        </div>

        {/* FILTER */}
        <div className="hk-filters">
          <div className="hk-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="hk-search-ic" />
            <input
              type="text"
              placeholder="Cari mesin / PIC / material..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="hk-date">
            <label>Dari</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="hk-date">
            <label>Sampai</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          {(search || dateFrom || dateTo) && (
            <button className="hk-reset" onClick={resetFilter}>
              Reset
            </button>
          )}
        </div>

        {/* TABLE */}
        <div className="hk-table-wrap">
          <table className="hk-table">
            <thead>
              <tr>
                <th style={{ width: 60, textAlign: "center" }}>No</th>
                <th>Tanggal</th>
                <th>Mesin</th>
                <th>PIC</th>
                <th>Material</th>
                <th style={{ textAlign: "center" }}>Qty</th>
                <th>Keterangan</th>
                <th>Created At</th>
                <th style={{ textAlign: "center" }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="hk-empty">
                    Memuat data...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="hk-empty">
                    Tidak ada data ditemukan
                  </td>
                </tr>
              ) : (
                filtered.map((row, index) => (
                  <tr key={row.id}>
                    <td className="hk-center hk-muted">{index + 1}</td>
                    <td>
                      {row.tanggal
                        ? new Date(row.tanggal).toLocaleDateString("id-ID")
                        : "-"}
                    </td>
                    <td className="hk-bold cell-left">{row.mesin}</td>
                    <td>{row.pic}</td>
                    <td className="hk-bold">{row.material_name}</td>
                    <td className="hk-center">
                      <span className="hk-qty">{row.qty}</span>
                    </td>
                    <td>{row.note || "-"}</td>
                    <td className="hk-muted">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString("id-ID")
                        : "-"}
                    </td>
                    <td className="hk-center">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(row.id)}
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
